import { syncBaseUrl } from "../database/onlineDb/baseUrl";

const ID = /^[0-9a-f]{32}$/;
const DEFAULT_TIMEOUT_MS = 15000;
const own = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const record = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const IDENTITY_KEYS = ["permissionId", "reservationId", "unitSequence", "memberSetSequence", "guid", "laterality", "s3Bucket", "s3Key"];
const SUPPORTED_IMAGE_CONTENT_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
export const RESERVATION_IMAGE_ACCEPT = "image/jpeg,image/png,image/webp";
export const isSupportedReservationImageFile = (file) =>
  Boolean(file && SUPPORTED_IMAGE_CONTENT_TYPES.has(typeof file.type === "string" ? file.type : ""));
const exactPermission = (value) => record(value) &&
  [...IDENTITY_KEYS, "permissionExpiresAt", "permissionRevokedAt", "objectWriteNonce"].every((key) => own(value, key)) &&
  Object.keys(value).length === 11 && ID.test(value.permissionId) && ID.test(value.reservationId) && ID.test(value.objectWriteNonce) &&
  Number.isSafeInteger(value.unitSequence) && value.unitSequence > 0 && Number.isSafeInteger(value.memberSetSequence) && value.memberSetSequence > 0 &&
  typeof value.guid === "string" && value.guid.length > 0 && ["left", "right"].includes(value.laterality) &&
  typeof value.s3Bucket === "string" && value.s3Bucket.length > 0 && typeof value.s3Key === "string" && value.s3Key.length > 0 &&
  typeof value.permissionExpiresAt === "string" && value.permissionRevokedAt === null;

class ReservationS3UploadError extends Error {
  constructor(code, retryable) {
    super(code === "UPLOAD_OUTCOME_UNKNOWN" ? "The upload outcome is unknown. Reconcile before trying again." : "Reservation-authorized image upload failed.");
    this.name = "ReservationS3UploadError";
    this.code = code;
    this.retryable = retryable;
  }
}
const safeError = () => new ReservationS3UploadError("UPLOAD_FAILED", false);
const unknownError = () => new ReservationS3UploadError("UPLOAD_OUTCOME_UNKNOWN", true);
const notVerifiedError = () => new ReservationS3UploadError("UPLOAD_NOT_VERIFIED", false);
const objectAbsentError = () => new ReservationS3UploadError("UPLOAD_OBJECT_ABSENT", true);

export const reconcileReservationS3Upload = async ({ baseUrl = syncBaseUrl, token, permission, fetchImpl, requestTimeoutMs = DEFAULT_TIMEOUT_MS } = {}) => {
  if (typeof token !== "string" || token.length === 0 || !exactPermission(permission)) throw safeError();
  const request = fetchImpl || (typeof window !== "undefined" && window.fetch ? window.fetch.bind(window) : null);
  if (!request || !Number.isFinite(requestTimeoutMs) || requestTimeoutMs <= 0) throw safeError();
  const identity = IDENTITY_KEYS.reduce((result, key) => ({ ...result, [key]: permission[key] }), {});
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  let timer;
  try {
    const completion = await Promise.race([
      (async () => {
        const completionResponse = await request(`${baseUrl}/s3/upload-complete`, {
          method: "POST",
          ...(controller ? { signal: controller.signal } : {}),
          headers: { Accept: "application/json", Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify(identity),
        });
        if (!completionResponse.ok) {
          if (completionResponse.status === 409) {
            try {
              if ((await completionResponse.json())?.code === "OBJECT_ABSENT") throw objectAbsentError();
            } catch (error) {
              if (error instanceof ReservationS3UploadError) throw error;
            }
            throw notVerifiedError();
          }
          throw unknownError();
        }
        return completionResponse.json();
      })(),
      new Promise((resolve, reject) => {
        timer = setTimeout(() => {
          if (controller) controller.abort();
          reject(unknownError());
        }, requestTimeoutMs);
      }),
    ]);
    if (!record(completion) || completion.success !== true || completion.permissionId !== permission.permissionId ||
      completion.guid !== permission.guid || completion.bucketName !== permission.s3Bucket || completion.key !== permission.s3Key) throw unknownError();
    return completion;
  } catch (error) {
    if (error instanceof ReservationS3UploadError) throw error;
    throw unknownError();
  } finally {
    clearTimeout(timer);
  }
};

export const reservationS3Upload = async ({ baseUrl = syncBaseUrl, token, permission, file, fetchImpl, requestTimeoutMs = DEFAULT_TIMEOUT_MS } = {}) => {
  if (typeof token !== "string" || token.length === 0 || !exactPermission(permission) || !file) throw safeError();
  const contentType = typeof file.type === "string" ? file.type : "";
  if (!isSupportedReservationImageFile(file)) throw safeError();
  const request = fetchImpl || (typeof window !== "undefined" && window.fetch ? window.fetch.bind(window) : null);
  if (!request || !Number.isFinite(requestTimeoutMs) || requestTimeoutMs <= 0) throw safeError();
  const authenticatedHeaders = { Accept: "application/json", Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const identity = IDENTITY_KEYS.reduce((result, key) => ({ ...result, [key]: permission[key] }), {});
  const body = JSON.stringify(identity);
  const sessionBody = JSON.stringify({ ...identity, contentType });

  const bounded = async (operation, outcomeUnknown) => {
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    let timer;
    try {
      return await Promise.race([
        operation(controller && controller.signal),
        new Promise((resolve, reject) => {
          timer = setTimeout(() => {
            if (controller) controller.abort();
            reject(outcomeUnknown ? unknownError() : safeError());
          }, requestTimeoutMs);
        }),
      ]);
    } catch (error) {
      if (error instanceof ReservationS3UploadError) throw error;
      throw outcomeUnknown ? unknownError() : safeError();
    } finally {
      clearTimeout(timer);
    }
  };

  try {
    const session = await bounded(async (signal) => {
      const sessionResponse = await request(`${baseUrl}/s3/upload-session`, { method: "POST", ...(signal ? { signal } : {}), headers: authenticatedHeaders, body: sessionBody });
      if (!sessionResponse.ok) throw safeError();
      return sessionResponse.json();
    }, false);
    if (!record(session) || session.success !== true || !record(session.upload) || typeof session.upload.url !== "string" || session.upload.url.length === 0 ||
      !record(session.upload.fields) || session.upload.fields.key !== permission.s3Key ||
      session.upload.fields["Content-Type"] !== contentType ||
      session.upload.fields["x-amz-meta-reservation-object-nonce"] !== permission.objectWriteNonce ||
      session.objectWriteNonce !== permission.objectWriteNonce || session.permissionId !== permission.permissionId ||
      session.bucketName !== permission.s3Bucket || session.key !== permission.s3Key) throw safeError();

    // advenio-bfex — an aborted/timed-out S3 form POST (the prod incident's
    // ERR_ABORTED) leaves the object absent while the permission stays valid.
    // Each attempt gets its own bounded window; one retry before surfacing.
    const s3FormPost = () => bounded(async (signal) => {
      const uploadBody = new FormData();
      Object.entries(session.upload.fields).forEach(([key, value]) => uploadBody.append(key, value));
      uploadBody.append("file", file);
      const uploadResponse = await request(session.upload.url, { method: "POST", ...(signal ? { signal } : {}), body: uploadBody });
      if (!uploadResponse.ok) throw unknownError();
    }, true);
    try {
      await s3FormPost();
    } catch (uploadError) {
      try {
        await s3FormPost();
      } catch {
        throw uploadError;
      }
    }

    const completeOnce = () => bounded(async (signal) => {
      const completionResponse = await request(`${baseUrl}/s3/upload-complete`, { method: "POST", ...(signal ? { signal } : {}), headers: authenticatedHeaders, body });
      if (!completionResponse.ok) throw unknownError();
      return completionResponse.json();
    }, true);

    let completion;
    try {
      completion = await completeOnce();
    } catch (completionError) {
      if (completionError?.code !== "UPLOAD_OUTCOME_UNKNOWN") throw completionError;
      // upload-complete 409/timeout right after a 204 S3 POST = the object
      // never landed (aborted form POST, lost nonce metadata). One fresh S3
      // POST with the same still-valid permission, then re-run complete.
      try {
        await s3FormPost();
      } catch {
        throw completionError;
      }
      completion = await completeOnce();
    }
    if (!record(completion) || completion.success !== true || completion.permissionId !== permission.permissionId ||
      completion.guid !== permission.guid || completion.bucketName !== permission.s3Bucket || completion.key !== permission.s3Key) throw unknownError();
    return completion;
  } catch (error) {
    if (error instanceof ReservationS3UploadError) throw error;
    throw safeError();
  }
};
